import { useEffect, useMemo, useState } from 'react'
import { Menu, Eye, EyeOff, Save, Info } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import PageHeader from '@/components/PageHeader'
import { menuVisibilityAPI } from '@/lib/api'
import { NAV_SECTIONS } from '@/components/Layout'
import { useAuth } from '@/hooks/useAuth'
import usePageTitle from '@/lib/usePageTitle'

/* Menu-zichtbaarheid — per item aan/uit voor deelnemers.
   Los van de module-toggle: die schakelt de hele module + routes uit,
   hier verbergen we enkel het sidebar-item. Admins zien verborgen items
   nog altijd in hun eigen sidebar met een oog-uit badge.

   Use case: kempenvision-item verbergen tot de show start, dan zichtbaar. */

export default function MenuVisibilityAdminPage() {
  usePageTitle('Menu-zichtbaarheid — beheer')
  const { user } = useAuth()
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin'
  const [hidden,  setHidden]  = useState(new Set())
  const [loading, setLoading] = useState(true)
  const [saving,  setSaving]  = useState(null)   // path van item dat aan het opslaan is
  const [err,     setErr]     = useState(null)

  useEffect(() => {
    if (!isAdmin) return
    menuVisibilityAPI.getHidden()
      .then(r => setHidden(new Set(r.data.hidden || [])))
      .catch(e => setErr(e.response?.data?.error || e.message))
      .finally(() => setLoading(false))
  }, [isAdmin])

  const items = useMemo(() => {
    // Flatten alle secties → één lijst { groupLabel, item }
    const out = []
    for (const sec of NAV_SECTIONS) {
      for (const it of (sec.items || [])) {
        if (!it.path) continue
        out.push({
          groupLabel: sec.label || '(top)',
          path: it.path,
          label: it.label,
          moduleKey: it.moduleKey || null,
          adminOnly: !!it.adminOnly,
          helpdeskOnly: !!it.helpdeskOnly,
        })
      }
    }
    return out
  }, [])

  const grouped = useMemo(() => {
    const g = {}
    for (const it of items) { (g[it.groupLabel] ||= []).push(it) }
    return g
  }, [items])

  const toggle = async (path) => {
    const willHide = !hidden.has(path)
    setSaving(path)
    // Optimistische update — bij fout draaien we terug
    setHidden(prev => {
      const nw = new Set(prev)
      willHide ? nw.add(path) : nw.delete(path)
      return nw
    })
    try {
      await menuVisibilityAPI.adminSet(path, willHide)
    } catch (e) {
      alert('Opslaan mislukt: ' + (e.response?.data?.error || e.message))
      setHidden(prev => {
        const nw = new Set(prev)
        willHide ? nw.delete(path) : nw.add(path)
        return nw
      })
    } finally { setSaving(null) }
  }

  if (!isAdmin) return <div className="p-6 text-muted-foreground">Enkel voor admins.</div>
  if (loading) return <div className="p-6 text-muted-foreground">Laden…</div>

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <PageHeader
        icon={Menu}
        eyebrow="Beheer"
        title="Menu-zichtbaarheid"
        subtitle="Verberg individuele sidebar-items voor deelnemers zonder de module uit te schakelen."
      />

      {err && <div className="text-signal text-sm">{err}</div>}

      <div className="flex items-start gap-2 text-xs text-muted-foreground bg-card border border-border rounded p-3">
        <Info className="h-4 w-4 shrink-0 mt-0.5 text-acid" />
        <div>
          Verborgen items verdwijnen uit de sidebar van deelnemers. <strong>Admins</strong>
          {' '}blijven ze zien met een <EyeOff className="inline h-3 w-3 -mt-0.5" /> oog-uit
          badge zodat je de module kan blijven configureren/testen. De pagina zelf blijft
          bereikbaar via directe URL — enkel het menu-item wordt verborgen.
          Deelnemers zien wijzigingen binnen 60 s zonder te refreshen.
        </div>
      </div>

      {Object.entries(grouped).map(([groupLabel, gs]) => (
        <Card key={groupLabel}>
          <CardContent className="p-0">
            <div className="px-4 py-2 border-b border-border font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              {groupLabel}
            </div>
            <ul className="divide-y divide-border/40">
              {gs.map(it => {
                const isHidden = hidden.has(it.path)
                const busy = saving === it.path
                return (
                  <li key={it.path} className="flex items-center gap-3 px-4 py-2.5">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold flex items-center gap-2">
                        {it.label}
                        {it.adminOnly && <Badge variant="outline" className="text-[9px]">admin-only</Badge>}
                        {it.helpdeskOnly && <Badge variant="outline" className="text-[9px]">helpdesk</Badge>}
                      </div>
                      <div className="text-[11px] text-muted-foreground font-mono">
                        {it.path}
                        {it.moduleKey && <span className="ml-2">· module: {it.moduleKey}</span>}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => toggle(it.path)}
                      disabled={busy || it.adminOnly || it.helpdeskOnly}
                      title={it.adminOnly || it.helpdeskOnly
                        ? 'Rol-gebonden item — deelnemers zien dit sowieso al niet'
                        : (isHidden ? 'Terug zichtbaar maken voor deelnemers' : 'Verbergen voor deelnemers')}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-mono uppercase tracking-wider border transition-colors ${
                        isHidden
                          ? 'bg-signal/15 border-signal/40 text-signal hover:bg-signal/25'
                          : 'bg-live/15 border-live/40 text-live hover:bg-live/25'
                      } disabled:opacity-40 disabled:cursor-not-allowed`}
                    >
                      {isHidden
                        ? <><EyeOff className="h-3.5 w-3.5" /> verborgen</>
                        : <><Eye    className="h-3.5 w-3.5" /> zichtbaar</>}
                    </button>
                  </li>
                )
              })}
            </ul>
          </CardContent>
        </Card>
      ))}
    </div>
  )
}
